import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useLocalStorage } from '@xing-com/crate-hooks-use-web-storage';
import { useExperiment } from '@xing-com/hub';
import { BodyCopy } from '@xing-com/typography';

import { LoadingScreen } from './components/loading-screen';
import * as S from './swimp-section.styles';
import { useTrackingSwimp } from './tracking';

enum FormStep {
  Role,
  Company,
  Loading,
}

const LOCAL_STORAGE_ROLE = 'losp-swimp-role';
const LOCAL_STORAGE_COMPANY = 'losp-swimp-company';

export const SwimpSection: React.FC = () => {
  const { formatMessage } = useIntl();
  const { trackOpenModal, trackCloseModal, trackSubmit } = useTrackingSwimp();
  const [isModal, setIsModal] = useState(false);
  const [formStep, setFormStep] = useState<FormStep>(FormStep.Role);

  const [roleLocalStorage, setRoleLocalStorage] = useLocalStorage(
    LOCAL_STORAGE_ROLE,
    ''
  );
  const [companyLocalStorage, setCompanyLocalStorage] = useLocalStorage(
    LOCAL_STORAGE_COMPANY,
    ''
  );
  const [role, setRole] = useState(roleLocalStorage);
  const [company, setCompany] = useState(companyLocalStorage);
  const fakeRoleInputRef = useRef<HTMLInputElement>(null);
  const roleInputRef = useRef<HTMLInputElement>(null);
  const companyInputRef = useRef<HTMLInputElement>(null);

  const showCompanyInput = formStep === FormStep.Company || !!company;
  const enableSubmit = !!role && showCompanyInput;

  const openModal = (): void => {
    trackOpenModal();
    setFormStep(FormStep.Role);
    setIsModal(true);
  };

  const closeModal = (): void => {
    trackCloseModal();
    setIsModal(false);
  };

  const moveCursorToEnd = (e: React.FocusEvent<HTMLInputElement>): void => {
    const input = e.target;
    const length = input.value.length;
    const isFullySelected =
      input.selectionStart === 0 && input.selectionEnd === length;

    if (isFullySelected) {
      input.setSelectionRange(length, length);
    }
  };

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();

    if (enableSubmit) {
      trackSubmit(!!company);
      setRoleLocalStorage(role);
      setCompanyLocalStorage(company);
      setFormStep(FormStep.Loading);
    } else if (formStep === FormStep.Role) {
      companyInputRef.current?.focus({ preventScroll: true });
    }
  };

  const experimentVariant = useExperiment('ABACUS-523');

  return (
    <>
      <S.MainContainer>
        {experimentVariant === 'C' ? (
          <>
            <S.RotatedContainer>
              <S.Marker>
                <FormattedMessage id="XDS_FLAG_NEW_NEUTRAL" />!
              </S.Marker>
              <S.BodyCopyVariantC>
                <FormattedMessage id="LOSP_SWIMP_SUBTITLE" />
              </S.BodyCopyVariantC>
              <S.HeroVariantC>
                <FormattedMessage id="LOSP_SWIMP_TITLE_C" />
                <S.InlineIllustrationVariantC />
              </S.HeroVariantC>
            </S.RotatedContainer>
            <S.SmallIllustrationVariantC />
            {/* @ts-expect-error FIXME: SC6 */}
            <S.LabelVariantC htmlFor="role-input">
              <FormattedMessage id="LOSP_SWIMP_ROLE_LABEL" />
            </S.LabelVariantC>
          </>
        ) : (
          <>
            <S.IllustrationVariantB
              tagline={formatMessage({ id: 'LOSP_SWIMP_TITLE_B' })}
            />
            <BodyCopy size="small" noMargin>
              <FormattedMessage id="LOSP_SWIMP_SUBTITLE" />
            </BodyCopy>
            {/* @ts-expect-error FIXME: SC6 */}
            <S.HeadlineVariantB htmlFor="role-input">
              <FormattedMessage id="LOSP_SWIMP_ROLE_LABEL" />
            </S.HeadlineVariantB>
            {/* @ts-expect-error FIXME: SC6 */}
            <S.HeroVariantB htmlFor="role-input">
              <FormattedMessage id="LOSP_SWIMP_ROLE_LABEL" />
            </S.HeroVariantB>
          </>
        )}
        <S.Input
          id="role-input"
          // @ts-expect-error FIXME: SC6
          readOnly
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Tab' || e.key === 'Escape') {
              return;
            }

            if (
              e.key.length === 1 &&
              !e.ctrlKey &&
              !e.metaKey &&
              e.key !== ' '
            ) {
              setRole(role + e.key);
            }

            openModal();

            const input = e.target as HTMLInputElement;
            input.blur();
          }}
          value={role}
          placeholder={formatMessage({ id: 'LOSP_SWIMP_ROLE_PLACEHOLDER' })}
          onClick={openModal}
          onClearInput={() => {
            setRole('');
            openModal();
          }}
          ref={fakeRoleInputRef}
          $isCursorHidden={true}
        />
      </S.MainContainer>

      <S.Modal
        isShown={isModal}
        isAnimated
        noGrid
        noMargin
        focusTrapProps={{
          focusTrapOptions: {
            initialFocus: '#role-input-modal',
            escapeDeactivates: true,
            onDeactivate: () => setIsModal(false),
          },
        }}
      >
        <S.ModalContent>
          <S.BackButton
            onClick={() => {
              closeModal();

              setTimeout(() => {
                fakeRoleInputRef.current?.focus();
                fakeRoleInputRef.current?.blur();
              }, 0);
            }}
            aria-label={formatMessage({ id: 'BUTTON_BACK' })}
          />
          {formStep === FormStep.Loading ? (
            <LoadingScreen
              role={role}
              company={company}
              backToInput={() => setFormStep(FormStep.Role)}
            />
          ) : (
            <S.Form onSubmit={onSubmit}>
              <S.InputWrapper $show>
                <BodyCopy noMargin sizeConfined="large">
                  <FormattedMessage id="LOSP_SWIMP_ROLE_LABEL" />
                </BodyCopy>
                <S.Input
                  id="role-input-modal"
                  value={role}
                  autoComplete="organization-title"
                  placeholder={formatMessage({
                    id: 'LOSP_SWIMP_ROLE_PLACEHOLDER',
                  })}
                  // @ts-expect-error FIXME: SC6
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setRole(e.target.value)
                  }
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                    setTimeout(() => {
                      moveCursorToEnd(e);
                    }, 0);

                    setFormStep(FormStep.Role);
                  }}
                  ref={roleInputRef}
                />
              </S.InputWrapper>

              <S.InputWrapper
                $show={showCompanyInput}
                aria-hidden={!showCompanyInput}
              >
                <BodyCopy noMargin sizeConfined="large">
                  <FormattedMessage id="LOSP_SWIMP_COMPANY_LABEL" />
                </BodyCopy>
                <S.Input
                  value={company}
                  autoComplete="organization"
                  placeholder={formatMessage({
                    id: 'LOSP_SWIMP_COMPANY_PLACEHOLDER',
                  })}
                  // @ts-expect-error FIXME: SC6
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCompany(e.target.value)
                  }
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                    moveCursorToEnd(e);
                    setFormStep(FormStep.Company);
                  }}
                  ref={companyInputRef}
                />
              </S.InputWrapper>
              <S.SubmitButton
                disabled={!role}
                type="submit"
                $showCompanyInput={showCompanyInput}
              >
                <FormattedMessage
                  id={
                    enableSubmit
                      ? 'JOBS_FIND_JOBS_SEARCH_BUTTON'
                      : 'LOSP_SWIMP_CONTINUE_BUTTON'
                  }
                />
              </S.SubmitButton>
            </S.Form>
          )}
        </S.ModalContent>
      </S.Modal>
      <S.NextSectionTop />
    </>
  );
};
